import { FunctionComponent, useContext } from "react";
import L, { DragEndEvent, type LeafletEventHandlerFnMap } from 'leaflet';
import { Marker, Popup, useMap } from 'react-leaflet';
import { TopologyMarkerIcon } from '../TopologyMarker/TopologyMarker';
import { TopologyMarkerDetailsPopup } from '../TopologyMarkerDetailsPopup/TopologyMarkerDetailsPopup';
import { GeographicCoordinates, L3ServiceLocationViewModel } from "../../model";
import { TopologyOverlayLeftColumnView } from "../Topology/Topology";
import { AppContext } from "../../../core/config";

interface L3ServiceLocationProperties {
  editedServiceItemId: number | null;
  draggedItemId: number | null;
  leftColumnView: TopologyOverlayLeftColumnView;
  location: L3ServiceLocationViewModel;
  setDraggedL3ServiceCoordinates: (latLng: L.LatLng | undefined) => void;
  setEditedServiceItemId: (itemId: number | null) => void;
  setDraggedItemId: (itemId: number | null) => void;
  setLeftColumnView: (leftColumnView: TopologyOverlayLeftColumnView) => void;
  setOriginalCoordinates: (coordinates: GeographicCoordinates) => void;
}

export const L3ServiceLocation: FunctionComponent<L3ServiceLocationProperties> = ({
  editedServiceItemId,
  draggedItemId,
  leftColumnView,
  location,
  setEditedServiceItemId,
  setDraggedItemId,
  setDraggedL3ServiceCoordinates,
  setLeftColumnView,
  setOriginalCoordinates
}) => {
  const { customerId } = useContext(AppContext);
  const map = useMap();
  const itemId = location.itemId;
  const isMarkerDragged = draggedItemId === itemId;
  const isMarkerSelected = editedServiceItemId === itemId;
  const markerEventsHandlers: LeafletEventHandlerFnMap = {
    dragend: (event: DragEndEvent) => {
      const target = event.target as L.Marker;
      const coordinates = target.getLatLng();
      if (location !== undefined) {
        location.coordinates = {
          latitude: coordinates.lat,
          longitude: coordinates.lng
        };
      }
      setDraggedL3ServiceCoordinates(coordinates);
    },
    popupopen: () => {
      setEditedServiceItemId(itemId);
    },
    popupclose: () => {
      setEditedServiceItemId(null);
    }
  };

  const onEditPositionClicked = (): void => {
    setLeftColumnView(TopologyOverlayLeftColumnView.ServicePositionChange);
    setDraggedItemId(itemId);
    setDraggedL3ServiceCoordinates(undefined);
    const originalCoordinates = location.coordinates;
    setOriginalCoordinates({
      latitude: originalCoordinates.latitude,
      longitude: originalCoordinates.longitude
    });
    map.closePopup();
  };

  return <Marker
    draggable={ isMarkerDragged }
    key={ `${itemId}.${isMarkerDragged}` }
    position={ [location.coordinates.latitude, location.coordinates.longitude] }
    icon={ TopologyMarkerIcon(location) }
    bubblingMouseEvents={ false }
    eventHandlers={ markerEventsHandlers }
  >
    <Popup>
      { customerId !== null && isMarkerSelected &&
      <TopologyMarkerDetailsPopup
        itemId={ itemId }
        customerId={ customerId }
        leftColumnView={ leftColumnView }
        onEditPositionClicked={ onEditPositionClicked }
      /> }
    </Popup>
  </Marker>;
};